import { FeatureFlag } from "@/types/appSettings";
import { debugLogger } from "@/utils/debug";
import { createOrUpdateFeatureFlag } from "@/services/appSettingsService";
import { platformApiFetch } from '@/services/platformApi/client';

const DEFAULT_FEATURE_FLAGS: FeatureFlag[] = [
  {
    id: 'default-google-auth',
    name: 'GOOGLE_AUTH',
    description: 'Enable Google authentication for user login',
    enabled: false
  },
  {
    id: 'default-apple-auth',
    name: 'APPLE_AUTH',
    description: 'Enable Apple authentication for user login',
    enabled: false
  },
  {
    id: 'default-email-notifications',
    name: 'EMAIL_NOTIFICATIONS',
    description: 'Enable email notifications for platform events',
    enabled: true
  },
  {
    id: 'default-fps-payments',
    name: 'FPS_PAYMENTS',
    description: 'Enable FPS payment method for subscriptions',
    enabled: true
  },
  {
    id: 'default-payme-payments',
    name: 'PAYME_PAYMENTS',
    description: 'Enable PayMe payment method for subscriptions',
    enabled: true
  },
  {
    id: 'default-enable-ads',
    name: 'ENABLE_ADS',
    description: 'Enable Google AdSense advertisements on the platform',
    enabled: false
  },
  {
    id: 'default-debug-mode',
    name: 'DEBUG_MODE',
    description: 'Enable debug logging and console output (automatically disabled in production)',
    enabled: false
  },
  {
    id: 'default-whatsapp-service-available',
    name: 'WHATSAPP_SERVICE_AVAILABLE',
    description: 'Master switch for WhatsApp-based auth and verification delivery',
    enabled: false
  },
  {
    id: 'default-enable-phone-login',
    name: 'ENABLE_PHONE_LOGIN',
    description: 'Enable passwordless phone login delivered through WhatsApp',
    enabled: false
  },
  {
    id: 'default-enable-whatsapp-mfa',
    name: 'ENABLE_WHATSAPP_MFA',
    description: 'Enable optional WhatsApp-based MFA challenges for users',
    enabled: false
  },
  {
    id: 'default-enable-automated-whatsapp-verification',
    name: 'ENABLE_AUTOMATED_WHATSAPP_VERIFICATION',
    description: 'Automatically send paid-provider WhatsApp verification codes instead of using the manual admin flow',
    enabled: false
  },
  {
    id: 'default-enable-multilingual',
    name: 'ENABLE_MULTILINGUAL',
    description: 'Enable multilingual UI and language switching across the platform',
    enabled: false
  },
  {
    id: 'default-social-facebook',
    name: 'ENABLE_SOCIAL_FACEBOOK',
    description: 'Enable Facebook social media link in footer',
    enabled: true
  },
  {
    id: 'default-social-instagram',
    name: 'ENABLE_SOCIAL_INSTAGRAM',
    description: 'Enable Instagram social media link in footer',
    enabled: true
  },
  {
    id: 'default-social-linkedin',
    name: 'ENABLE_SOCIAL_LINKEDIN',
    description: 'Enable LinkedIn social media link in footer',
    enabled: true
  },
  {
    id: 'default-social-twitter',
    name: 'ENABLE_SOCIAL_TWITTER',
    description: 'Enable Twitter social media link in footer',
    enabled: true
  }
];

const DEFAULT_FEATURE_FLAGS_BY_NAME = new Map(
  DEFAULT_FEATURE_FLAGS.map((flag) => [flag.name, flag])
);

/**
 * Get feature flags from the database
 */
export const getFeatureFlags = async (): Promise<FeatureFlag[]> => {
  try {
    const data = await platformApiFetch<Array<{ id: string; key: string; description: string | null; value: string }>>('/v1/settings/feature-flags');
    
    if (!data || data.length === 0) {
      return getDefaultFeatureFlags();
    }

    const databaseFlags = data.map((setting) => ({
      id: setting.id,
      name: setting.key,
      description: setting.description || getFeatureDescription(setting.key),
      enabled: setting.value === 'true'
    }));

    const mergedDefaultFlags = DEFAULT_FEATURE_FLAGS.map((defaultFlag) => {
      const databaseFlag = databaseFlags.find((flag) => flag.name === defaultFlag.name);
      return databaseFlag ?? defaultFlag;
    });

    const extraDatabaseFlags = databaseFlags.filter(
      (flag) => !DEFAULT_FEATURE_FLAGS_BY_NAME.has(flag.name)
    );

    return [...mergedDefaultFlags, ...extraDatabaseFlags];
  } catch (error) {
    debugLogger.error("Error in getFeatureFlags:", error);
    return getDefaultFeatureFlags();
  }
};

/**
 * Update a feature flag in the database
 */
export const updateFeatureFlag = async (flagId: string, enabled: boolean): Promise<void> => {
  try {
    const defaultFlag = DEFAULT_FEATURE_FLAGS.find((flag) => flag.id === flagId);

    if (defaultFlag) {
      await createOrUpdateFeatureFlag(
        defaultFlag.name,
        enabled.toString(),
        defaultFlag.description
      );
      return;
    }
    await platformApiFetch(`/v1/settings/${flagId}`, {
      method: 'PUT',
      body: JSON.stringify({ value: enabled.toString() }),
    });
  } catch (error) {
    debugLogger.error("Error in updateFeatureFlag:", error);
    throw error;
  }
};

/**
 * Get specific feature flag value by name
 */
export const getFeatureFlagValue = async (flagName: string): Promise<boolean> => {
  try {
    const data = await platformApiFetch<string | null>(`/v1/settings/key/${encodeURIComponent(flagName)}`);

    if (!data) {
      debugLogger.warn(`Feature flag ${flagName} not found, using default`);
      return getDefaultFeatureFlagValue(flagName);
    }
    
    return data === 'true';
  } catch (error) {
    debugLogger.error(`Error getting feature flag ${flagName}:`, error);
    return getDefaultFeatureFlagValue(flagName);
  }
};

/**
 * Get default feature flags when database is unavailable
 */
const getDefaultFeatureFlags = (): FeatureFlag[] => {
  return DEFAULT_FEATURE_FLAGS;
};

/**
 * Get default value for a specific feature flag
 */
const getDefaultFeatureFlagValue = (flagName: string): boolean => {
  switch (flagName) {
    case 'GOOGLE_AUTH':
    case 'APPLE_AUTH':
    case 'ENABLE_ADS':
    case 'DEBUG_MODE':
    case 'WHATSAPP_SERVICE_AVAILABLE':
    case 'ENABLE_PHONE_LOGIN':
    case 'ENABLE_WHATSAPP_MFA':
    case 'ENABLE_AUTOMATED_WHATSAPP_VERIFICATION':
    case 'ENABLE_MULTILINGUAL':
      return false;
    case 'EMAIL_NOTIFICATIONS':
    case 'FPS_PAYMENTS':
    case 'PAYME_PAYMENTS':
    case 'ENABLE_SOCIAL_FACEBOOK':
    case 'ENABLE_SOCIAL_INSTAGRAM':
    case 'ENABLE_SOCIAL_LINKEDIN':
    case 'ENABLE_SOCIAL_TWITTER':
      return true;
    default:
      return false;
  }
};

/**
 * Get user-friendly description for each feature flag
 */
const getFeatureDescription = (featureName: string): string => {
  switch (featureName) {
    case 'GOOGLE_AUTH':
      return 'Enable Google authentication for user login';
    case 'APPLE_AUTH':
      return 'Enable Apple authentication for user login';
    case 'EMAIL_NOTIFICATIONS':
      return 'Enable email notifications for platform events';
    case 'FPS_PAYMENTS':
      return 'Enable FPS payment method for subscriptions';
    case 'PAYME_PAYMENTS':
      return 'Enable PayMe payment method for subscriptions';
    case 'ENABLE_ADS':
      return 'Enable Google AdSense advertisements on the platform';
    case 'DEBUG_MODE':
      return 'Enable debug logging and console output (automatically disabled in production)';
    case 'WHATSAPP_SERVICE_AVAILABLE':
      return 'Master switch for WhatsApp-based auth and verification delivery';
    case 'ENABLE_PHONE_LOGIN':
      return 'Enable passwordless phone login delivered through WhatsApp';
    case 'ENABLE_WHATSAPP_MFA':
      return 'Enable optional WhatsApp-based MFA challenges for users';
    case 'ENABLE_AUTOMATED_WHATSAPP_VERIFICATION':
      return 'Automatically send paid-provider WhatsApp verification codes instead of using the manual admin flow';
    case 'ENABLE_MULTILINGUAL':
      return 'Enable multilingual UI and language switching across the platform';
    case 'ENABLE_SOCIAL_FACEBOOK':
      return 'Enable Facebook social media link in footer';
    case 'ENABLE_SOCIAL_INSTAGRAM':
      return 'Enable Instagram social media link in footer';
    case 'ENABLE_SOCIAL_LINKEDIN':
      return 'Enable LinkedIn social media link in footer';
    case 'ENABLE_SOCIAL_TWITTER':
      return 'Enable Twitter social media link in footer';
    default:
      return `Toggle for ${featureName} feature`;
  }
};
